<script lang="ts">
const __DsBlock = {
	icon: 'lucide:tag',
	description: '$t:blocks.badge.description',
	category: 'basic',
	options: [
		{
			name: 'label',
			label: '$t:blocks.common.props.label',
		},
		{
			name: 'color',
			label: '$t:blocks.common.props.color',
			block: BuiltinBlockIds.InputSemanticColor,
		},
		{
			name: 'variant',
			label: '$t:blocks.common.props.variant',
			type: 'enum',
			values: ['solid', 'outline', 'soft', 'subtle'],
		},
		{
			name: 'size',
			label: '$t:blocks.common.props.size',
			type: 'enum',
			values: ['xs', 'sm', 'md', 'lg'],
		},
		{
			name: 'icon',
			label: '$t:blocks.common.props.icon',
			block: BuiltinBlockIds.InputIcon,
			nullable: true,
		},
		{
			name: 'rounded',
			label: '$t:blocks.common.props.rounded',
			type: 'enum',
			values: Object.values(RoundedVariant),
		},
	],
}
</script>
<script lang="ts" setup>
import type { BadgeProps } from '#layers/ui/public';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { computed } from 'vue';
import { defineBlock } from '#layers/block-engine/public';
import { isColorValid, RoundedVariant } from '#layers/ui/public';
import { isSemanticColorValue } from './Input/InputSemanticColor.block.vue';

interface BadgeBlockProps extends Pick<BadgeProps, 'color' | 'variant' | 'size' | 'icon' | 'customColor' | 'rounded'> {
	label?: string;
}

const {
	label = 'Badge',
	color: colorProp = 'neutral',
	variant = 'soft',
	size = 'md',
	rounded = RoundedVariant.Full,
	customColor: customColorProp,
	...forwardedProps
} = defineProps<BadgeBlockProps>();

;

const badgeProps = computed(() => {
	const colorValue = colorProp;
	const isInlineCustomColor = typeof colorValue === 'string'
		&& !isSemanticColorValue(colorValue)
		&& colorValue !== 'custom'
		&& isColorValid(colorValue);
	const customColor = colorValue === 'custom'
		? customColorProp
		: (isInlineCustomColor ? colorValue : undefined);
	const color: BadgeProps['color'] = customColor
		? 'custom'
		: (typeof colorValue === 'string' && isSemanticColorValue(colorValue) ? colorValue : 'neutral');

	return {
		...forwardedProps,
		label,
		variant,
		size,
		rounded,
		color,
		customColor,
	};
});
</script>

<template>
	<!-- Blocks should be block elements by default so I wrap the badge in a div -->
	<div class="flex justify-center">
		<DBadge v-bind="badgeProps" />
	</div>
</template>
